import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Table,
  Badge,
  Alert,
  Spinner
} from 'react-bootstrap';
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaSearch,
  FaCheckCircle,
  FaTimesCircle
} from 'react-icons/fa';
import SearchViewApi from '../../../api/SearchViewApi';
import ObjectConfigApi from '../../../api/ObjectConfigApi';
import SearchViewDialog from './SearchViewDialog';
import PubSub from 'pubsub-js';
import { getEntitlementsSection } from '../../../utils/entitlements';

const SearchViewManager = () => {
  const navigate = useNavigate();
  const searchViewEnt = getEntitlementsSection('searchView', {});
  const allowViewSearchView = searchViewEnt.allowViewSearchView !== false;
  const allowCreateSearchView = searchViewEnt.allowCreateSearchView !== false;
  const allowedObjectsList = Array.isArray(searchViewEnt.allowedObjects) ? searchViewEnt.allowedObjects : null;
  const allowedObjectsSet = useMemo(
    () => (allowedObjectsList === null ? null : new Set(allowedObjectsList.map((o) => (o || '').toLowerCase()))),
    [allowedObjectsList]
  );

  const [objects, setObjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editingObject, setEditingObject] = useState(null);
  const [searchViewData, setSearchViewData] = useState(null);

  const visibleObjects = useMemo(() => {
    if (allowedObjectsSet === null) return objects;
    return objects.filter((o) => allowedObjectsSet.has((o.object_name || '').toLowerCase()));
  }, [objects, allowedObjectsSet]);

  useEffect(() => {
    if (!allowViewSearchView) {
      navigate('/setup', { replace: true });
      return;
    }
    fetchObjectsWithStatus();

    // Subscribe to save events
    const token = PubSub.subscribe('SEARCH_VIEW_SAVED', () => {
      fetchObjectsWithStatus();
      setShowDialog(false);
      setEditingObject(null);
      setSearchViewData(null);
    });

    return () => {
      PubSub.unsubscribe(token);
    };
  }, [allowViewSearchView, navigate]);

  const fetchObjectsWithStatus = async () => {
    setLoading(true);
    setError('');
    try {
      const searchViewApi = new SearchViewApi();
      const response = await searchViewApi.getObjectsWithStatus();

      if (response?.data?.success) {
        setObjects(response.data.data || []);
      } else {
        setError(response?.data?.message || 'Failed to fetch objects');
      }
    } catch (err) {
      console.error('Error fetching objects with search view status:', err);
      setError(err.response?.data?.message || 'Failed to fetch objects');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingObject(null);
    setSearchViewData(null);
    setShowDialog(true);
  };

  const handleEdit = async (objectName) => {
    try {
      const searchViewApi = new SearchViewApi();
      const response = await searchViewApi.fetchByObjectName(objectName);

      if (response?.data?.success) {
        setSearchViewData(response.data.data);
        setEditingObject(objectName);
        setShowDialog(true);
      } else {
        setError('Failed to fetch search view configuration');
      }
    } catch (err) {
      console.error('Error fetching search view:', err);
      setError(err.response?.data?.message || 'Failed to fetch search view');
    }
  };

  const handleDelete = async (objectName, searchViewId) => {
    if (!window.confirm(`Are you sure you want to delete the search view for ${objectName}?`)) {
      return;
    }

    try {
      const searchViewApi = new SearchViewApi();
      const response = await searchViewApi.delete(searchViewId);

      if (response?.data?.success) {
        setSuccess('Search view deleted successfully');
        fetchObjectsWithStatus();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response?.data?.message || 'Failed to delete search view');
      }
    } catch (err) {
      console.error('Error deleting search view:', err);
      setError(err.response?.data?.message || 'Failed to delete search view');
    }
  };

  const handleDialogClose = () => {
    setShowDialog(false);
    setEditingObject(null);
    setSearchViewData(null);
  };

  if (!allowViewSearchView) return null;

  if (loading) {
    return (
      <Container fluid className="mt-4">
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-2">Loading search views...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4">
      <Row>
        <Col>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h4 className="mb-0">
                <FaSearch className="me-2" />
                Search View Configuration
              </h4>
              {allowCreateSearchView && (
                <Button variant="primary" onClick={handleCreate}>
                  <FaPlus className="me-2" />
                  Configure Search View
                </Button>
              )}
            </Card.Header>
            <Card.Body>
              {error && (
                <Alert variant="danger" dismissible onClose={() => setError('')}>
                  {error}
                </Alert>
              )}
              {success && (
                <Alert variant="success" dismissible onClose={() => setSuccess('')}>
                  {success}
                </Alert>
              )}

              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Object</th>
                    <th>Status</th>
                    <th>Display Fields</th>
                    <th>Filterable Fields</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleObjects.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="text-center text-muted">
                        {allowCreateSearchView ? 'No objects found. Configure a search view to get started.' : 'No objects available for search views.'}
                      </td>
                    </tr>
                  ) : (
                    visibleObjects.map((obj) => (
                      <tr key={obj.object_name}>
                        <td>
                          <strong>{obj.object_label || obj.object_name}</strong>
                          <br />
                          <small className="text-muted">{obj.object_name}</small>
                        </td>
                        <td>
                          {obj.has_search_view ? (
                            <Badge bg="success">
                              <FaCheckCircle className="me-1" />
                              Configured
                            </Badge>
                          ) : (
                            <Badge bg="secondary">
                              <FaTimesCircle className="me-1" />
                              Not Configured
                            </Badge>
                          )}
                        </td>
                        <td>
                          {obj.has_search_view ? (
                            <Badge bg="info">Configured</Badge>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>
                          {obj.has_search_view ? (
                            <Badge bg="info">Configured</Badge>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>
                          {allowCreateSearchView && (
                            obj.has_search_view ? (
                              <>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  className="me-2"
                                  onClick={() => handleEdit(obj.object_name)}
                                >
                                  <FaEdit className="me-1" />
                                  Edit
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleDelete(obj.object_name, obj.search_view_id)}
                                >
                                  <FaTrash className="me-1" />
                                  Delete
                                </Button>
                              </>
                            ) : (
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={() => {
                                  setEditingObject(obj.object_name);
                                  setShowDialog(true);
                                }}
                              >
                                <FaPlus className="me-1" />
                                Configure
                              </Button>
                            )
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <SearchViewDialog
        show={showDialog}
        onClose={handleDialogClose}
        objectName={editingObject}
        searchViewData={searchViewData}
      />
    </Container>
  );
};

export default SearchViewManager;
